<script setup lang="ts" generic="T">
import { computed, onBeforeUnmount, provide, ref } from "vue";
import type { Ref } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";

import { toggleKey } from "./injectKeys";

defineOptions({ inheritAttrs: false });

const model = defineModel<T>({ required: true });

const items = ref([]) as Ref<Array<{ value: T; el: HTMLElement }>>;

// Item widths can change after mount (font swap, content updates) — re-measure
// the active indicator whenever any item resizes instead of caching mount-time
// geometry.
const measureTick = ref(0);
const resizeObserver = new ResizeObserver(() => measureTick.value++);
onBeforeUnmount(() => resizeObserver.disconnect());

provide(toggleKey, {
  register: (value, elRef) => {
    const el = elRef.value!;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- eslint erases the SFC generic and sees a no-op, but vue-tsc needs the cast to T
    items.value.push({ value: value as T, el });
    resizeObserver.observe(el);
  },
  // A stale entry would win the `find` below and measure a detached element.
  unregister: (value) => {
    const index = items.value.findIndex((item) => item.value === value);
    if (index === -1) return;
    resizeObserver.unobserve(items.value[index].el);
    items.value.splice(index, 1);
  },
  active: model,
});

const activeItem = computed(() => {
  void measureTick.value;
  const item = items.value.find((item) => item.value === model.value);
  return item && { left: item.el.offsetLeft, width: item.el.offsetWidth };
});

function selectNext() {
  const index = items.value.findIndex((item) => item.value === model.value);
  model.value = items.value[(index + 1) % items.value.length].value;
}
</script>

<template>
  <BaseTooltip>
    <template v-if="$slots.tooltip" #content>
      <slot name="tooltip" />
    </template>
    <button class="toggle-button" v-bind="$attrs" @click="selectNext">
      <div
        v-if="activeItem"
        class="active-indicator"
        :style="{ left: `${activeItem.left}px`, width: `${activeItem.width}px` }"
      />
      <slot />
    </button>
  </BaseTooltip>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

$button-padding: 2px;

.toggle-button {
  @include typography.medium("semi-bold");

  cursor: pointer;
  outline: revert;
  outline-offset: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $button-padding;
  border: 1px solid colors.$border-subtle;
  border-radius: 6px;
  padding: $button-padding;
  position: relative;
  height: 36px;

  .active-indicator {
    content: "";
    position: absolute;
    inset: $button-padding;
    background-color: colors.$main;
    border-radius: 4px;
    transition: all 0.3s ease;
  }
}
</style>
